import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import * as lucide from "lucide";

import type { NoteSummary, SearchHit } from "../../api/schemas/notes";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import { relativeTime } from "../../widgets/a2aUi";
import {
	type FolderNode,
	buildTree,
	countsTotal,
	folderName,
	folderOf,
	folderTreeView,
	isUnder,
	treeFromCounts,
} from "../../widgets/folderTree";
import { icon } from "../../widgets/icons";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { FOLDER_NAME_ID, knowledgeHref } from "./update";

type H = HtmlBuilder<Message>;

const glyph = (
	h: H,
	name:
		| "Search"
		| "Plus"
		| "NotebookText"
		| "Folder"
		| "FolderPlus"
		| "FolderInput"
		| "Pencil"
		| "ChevronRight"
		| "GripVertical",
) => icon(h, name, lucide[name], { size: 16, class: "shrink-0" });

const noteHref = (shared: Shared, id: string) =>
	linkHref(shared.route, Route.Note({ id, asOf: Option.none() }));

const recordHref = (shared: Shared, id: string) =>
	linkHref(
		shared.route,
		Route.Record({ id, asOf: Option.none(), via: Option.none() }),
	);

/**
 * The server marks matches with `**`. Splitting on it gives text at even
 * indexes and matches at odd ones; every piece is a text node, so markup in a
 * note (`<script>`) shows as the characters it is.
 */
export const highlight = (
	h: H,
	snippet: string,
): ReadonlyArray<Html | string> =>
	snippet
		.split("**")
		.map((part, i) =>
			i % 2 === 1
				? h.mark([h.Class("bg-warning/40 rounded-xs px-0.5")], [part])
				: part,
		);

const tagBadges = (h: H, tags: ReadonlyArray<string>): Html =>
	h.ul(
		[h.Class("flex flex-nowrap gap-1"), h.AriaLabel(i18next.t("entry.tags"))],
		tags.map((tag) =>
			h.li([h.Key(tag), h.Class("badge badge-ghost badge-sm")], [`#${tag}`]),
		),
	);

const moveMenu = (
	h: H,
	note: NoteSummary,
	nodes: ReadonlyArray<FolderNode>,
): Html => {
	const title = note.title || i18next.t("knowledge.list.untitled");
	const option = (path: string, label: string) =>
		h.option(
			[h.Key(path), h.Value(path), h.Selected(folderOf(note) === path)],
			[label],
		);
	return h.div(
		[
			h.DataAttribute("testid", "knowledge-move-menu"),
			h.Class("mt-2 flex flex-wrap items-center gap-2"),
		],
		[
			h.select(
				[
					h.Class("select select-sm w-auto min-w-48"),
					h.AriaLabel(i18next.t("knowledge.folders.moveTo", { title })),
					h.OnChange((folder) => Message.ChoseFolder({ id: note.id, folder })),
				],
				[
					option("", i18next.t("knowledge.folders.root")),
					...nodes.map((node) =>
						option(node.path, `${"\u2003".repeat(node.depth - 1)}${node.name}`),
					),
				],
			),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-ghost btn-sm"),
					h.AriaLabel(i18next.t("knowledge.folders.cancelMoveLabel")),
					h.OnClick(Message.ClickedMove({ id: note.id })),
				],
				[i18next.t("knowledge.folders.cancel")],
			),
		],
	);
};

const noteRow = (
	h: H,
	shared: Shared,
	model: Model,
	nodes: ReadonlyArray<FolderNode>,
	note: NoteSummary,
): Html => {
	const writable = Option.isNone(shared.asOf);
	const title = note.title || i18next.t("knowledge.list.untitled");
	const folder = folderOf(note);
	const dragged = Option.getOrNull(model.dragging) === note.id;
	return h.li(
		[
			h.Key(note.id),
			h.DataAttribute("testid", "knowledge-note"),
			h.Class(`group py-2 first:pt-0 last:pb-0 ${dragged ? "opacity-50" : ""}`),
			...(writable
				? [
						h.Draggable(true),
						h.OnDragStart(Message.StartedDrag({ id: note.id })),
						h.OnDragEnd(Message.EndedDrag()),
					]
				: []),
		],
		[
			h.div(
				[h.Class("flex items-baseline justify-between gap-3")],
				[
					// One line per note (title, folder, tags) so a long list scans at a glance.
					h.div(
						[h.Class("flex min-w-0 items-baseline gap-2")],
						[
							h.a(
								[
									h.Href(noteHref(shared, note.id)),
									h.Class("link link-hover font-medium min-w-0 truncate"),
								],
								[title],
							),
							// The note's folder, when the list shows more than that one folder.
							folder !== "" && folder !== Option.getOrNull(model.folder)
								? h.span(
										[
											h.DataAttribute("testid", "knowledge-note-folder"),
											h.Class(
												"flex shrink-0 items-center gap-1 text-xs text-base-content/60",
											),
										],
										[
											h.span([h.Class("opacity-70")], [glyph(h, "Folder")]),
											folder.split("/").join(" / "),
										],
									)
								: h.empty,
							note.tags.length === 0
								? h.empty
								: h.div(
										[h.Class("min-w-0 truncate")],
										[tagBadges(h, note.tags)],
									),
						],
					),
					h.div(
						[h.Class("flex shrink-0 items-center gap-2")],
						[
							h.span(
								[h.Class("text-xs text-base-content/60 whitespace-nowrap")],
								[
									i18next.t("knowledge.list.updated", {
										time: relativeTime(note.lastUpdated, model.now),
									}),
								],
							),
							writable
								? h.button(
										[
											h.Type("button"),
											h.Class(
												"btn btn-ghost btn-xs btn-square text-base-content/60",
											),
											h.AriaLabel(
												i18next.t("knowledge.folders.moveLabel", { title }),
											),
											h.AriaExpanded(
												Option.getOrNull(model.moveMenu) === note.id,
											),
											h.Title(i18next.t("knowledge.folders.move")),
											h.OnClick(Message.ClickedMove({ id: note.id })),
										],
										[glyph(h, "FolderInput")],
									)
								: h.empty,
						],
					),
				],
			),
			note.snippet === ""
				? h.empty
				: h.p(
						[h.Class("mt-0.5 text-sm text-base-content/70 line-clamp-1")],
						[note.snippet],
					),
			writable && Option.getOrNull(model.moveMenu) === note.id
				? moveMenu(h, note, nodes)
				: h.empty,
		],
	);
};

const hitRow = (h: H, href: string, hit: SearchHit): Html =>
	h.li(
		[
			h.Key(hit.id),
			h.DataAttribute("testid", "knowledge-hit"),
			h.Class("py-3 first:pt-0 last:pb-0"),
		],
		[
			h.a([h.Href(href), h.Class("link link-hover font-medium")], [hit.title]),
			hit.snippet === ""
				? h.empty
				: h.p(
						[
							h.DataAttribute("testid", "knowledge-hit-snippet"),
							h.Class("mt-1 text-sm text-base-content/70 line-clamp-2"),
						],
						highlight(h, hit.snippet),
					),
		],
	);

const status = (h: H, text: string, testId: string, error = false): Html =>
	h.p(
		[
			h.DataAttribute("testid", testId),
			h.Role(error ? "alert" : "status"),
			h.Class(error ? "text-error text-sm" : "text-base-content/60 text-sm"),
		],
		[text],
	);

const hitGroup = (
	h: H,
	heading: string,
	hits: ReadonlyArray<SearchHit>,
	href: (id: string) => string,
): Html =>
	hits.length === 0
		? h.empty
		: h.section(
				[h.Class("space-y-2")],
				[
					h.h2(
						[
							h.Class(
								"text-xs font-semibold uppercase tracking-wide text-base-content/60",
							),
						],
						[`${heading} · ${hits.length}`],
					),
					h.ul(
						[h.Class("divide-y divide-base-300")],
						hits.map((hit) => hitRow(h, href(hit.id), hit)),
					),
				],
			);

const results = (h: H, model: Model, shared: Shared): Html =>
	AsyncData.match(model.search, {
		onIdle: () => h.empty,
		onLoading: () =>
			status(h, i18next.t("knowledge.list.searching"), "knowledge-searching"),
		onFailure: () =>
			status(
				h,
				i18next.t("knowledge.list.searchError"),
				"knowledge-error",
				true,
			),
		onSuccess: (data) => resultBody(h, model, shared, data),
		onRefreshing: (data) => resultBody(h, model, shared, data),
		onStale: ({ data }) => resultBody(h, model, shared, data),
	});

const resultBody = (
	h: H,
	model: Model,
	shared: Shared,
	data: { notes: ReadonlyArray<SearchHit>; records: ReadonlyArray<SearchHit> },
): Html =>
	data.notes.length + data.records.length === 0
		? status(
				h,
				i18next.t("knowledge.list.noResults", { query: model.query.trim() }),
				"knowledge-no-results",
			)
		: h.div(
				[h.DataAttribute("testid", "knowledge-results"), h.Class("space-y-6")],
				[
					hitGroup(
						h,
						i18next.t("knowledge.list.notesHeading"),
						data.notes,
						(id) => noteHref(shared, id),
					),
					hitGroup(
						h,
						i18next.t("knowledge.list.recordsHeading"),
						data.records,
						(id) => recordHref(shared, id),
					),
				],
			);

const newNoteButton = (h: H, model: Model, shared: Shared, primary: boolean) =>
	Option.isSome(shared.asOf)
		? h.empty
		: h.button(
				[
					h.Type("button"),
					h.Class(`btn btn-sm ${primary ? "btn-primary" : ""}`),
					h.AriaLabel(i18next.t("knowledge.list.newNoteLabel")),
					h.Disabled(model.creating),
					h.OnClick(Message.ClickedNewNote()),
				],
				[
					glyph(h, "Plus"),
					model.creating
						? i18next.t("knowledge.list.creating")
						: i18next.t("knowledge.list.newNote"),
				],
			);

/** Tags across the loaded notes, most used first, then by name. */
export const tagCounts = (
	notes: ReadonlyArray<NoteSummary>,
): ReadonlyArray<readonly [string, number]> => {
	const counts = new Map<string, number>();
	for (const note of notes)
		for (const tag of note.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
	return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
};

const tagFilter = (
	h: H,
	model: Model,
	tags: ReadonlyArray<readonly [string, number]>,
): Html => {
	const chip = (tag: string | null, text: string, label: string) => {
		const active = Option.getOrNull(model.tag) === tag;
		return h.button(
			[
				h.Key(tag ?? ""),
				h.Type("button"),
				h.Class(`btn btn-xs shrink-0 ${active ? "btn-neutral" : "btn-ghost"}`),
				h.AriaPressed(String(active)),
				h.AriaLabel(label),
				h.OnClick(Message.ChangedTag({ tag })),
			],
			[text],
		);
	};
	return tags.length === 0
		? h.empty
		: h.div(
				[
					h.Role("group"),
					h.AriaLabel(i18next.t("knowledge.list.tagsLabel")),
					// One scrolling line, not three rows of chips above the notes.
					h.Class("flex min-w-0 flex-nowrap gap-1 overflow-x-auto"),
				],
				[
					chip(
						null,
						i18next.t("knowledge.list.allTags"),
						i18next.t("knowledge.list.allTags"),
					),
					...tags.map(([tag, n]) =>
						chip(
							tag,
							`#${tag} ${n}`,
							i18next.t("knowledge.list.tagLabel", { tag }),
						),
					),
				],
			);
};

const emptyState = (h: H, model: Model, shared: Shared): Html =>
	h.div(
		[
			h.DataAttribute("testid", "knowledge-empty"),
			h.Class(
				"flex flex-col items-center gap-3 rounded-box border border-dashed border-base-300 px-6 py-12 text-center",
			),
		],
		[
			h.span([h.Class("text-base-content/40")], [glyph(h, "NotebookText")]),
			h.h2(
				[h.Class("font-semibold")],
				[i18next.t("knowledge.list.emptyTitle")],
			),
			h.p(
				[h.Class("max-w-sm text-sm text-base-content/70")],
				[i18next.t("knowledge.list.emptyBody")],
			),
			newNoteButton(h, model, shared, true),
		],
	);

/** Notes in the selected folder and below; every note when none is selected. */
export const inFolder = (
	notes: ReadonlyArray<NoteSummary>,
	folder: Option.Option<string>,
): ReadonlyArray<NoteSummary> =>
	Option.match(folder, {
		onNone: () => notes,
		onSome: (f) => notes.filter((n) => isUnder(folderOf(n), f)),
	});

const list = (
	h: H,
	model: Model,
	shared: Shared,
	all: ReadonlyArray<NoteSummary>,
	nodes: ReadonlyArray<FolderNode>,
): Html => {
	if (all.length === 0 && Option.isNone(model.folder))
		return emptyState(h, model, shared);
	const notes = inFolder(all, model.folder);
	if (notes.length === 0)
		return h.div(
			[h.Class("flex flex-wrap items-center justify-between gap-3")],
			[
				status(
					h,
					i18next.t("knowledge.folders.emptyFolder", {
						folder: Option.getOrElse(model.folder, () => ""),
					}),
					"knowledge-empty-folder",
				),
				newNoteButton(h, model, shared, false),
			],
		);
	const shown = Option.match(model.tag, {
		onNone: () => notes,
		onSome: (tag) => notes.filter((n) => n.tags.includes(tag)),
	});
	return shown.length === 0
		? status(
				h,
				i18next.t("knowledge.list.noTagMatches", {
					tag: Option.getOrElse(model.tag, () => ""),
				}),
				"knowledge-no-tag-matches",
			)
		: h.ul(
				[
					h.DataAttribute("testid", "knowledge-list"),
					h.Class("divide-y divide-base-300"),
				],
				shown.map((note) => noteRow(h, shared, model, nodes, note)),
			);
};

const notesOf = (model: Model): ReadonlyArray<NoteSummary> =>
	AsyncData.getOrElse(model.notes, () => ({ notes: [] })).notes;

/** "All notes / Projects / Atlas": every step but the last is a link. */
const breadcrumb = (h: H, shared: Shared, folder: string): Html => {
	const segments = folder.split("/");
	const crumbs = [
		{ path: null as string | null, label: i18next.t("knowledge.folders.all") },
		...segments.map((name, i) => ({
			path: segments.slice(0, i + 1).join("/") as string | null,
			label: name,
		})),
	];
	return h.nav(
		[
			h.DataAttribute("testid", "knowledge-breadcrumb"),
			h.AriaLabel(i18next.t("knowledge.folders.breadcrumbLabel")),
			h.Class("breadcrumbs py-0 text-sm"),
		],
		[
			h.ul(
				[],
				crumbs.map((crumb, i) =>
					h.li(
						[h.Key(crumb.path ?? "__all")],
						[
							i === crumbs.length - 1
								? h.span(
										[
											h.AriaCurrent("page"),
											h.Class("font-semibold text-base-content"),
										],
										[crumb.label],
									)
								: h.a(
										[
											h.Href(knowledgeHref(shared, crumb.path)),
											h.Class("text-base-content/70"),
										],
										[crumb.label],
									),
						],
					),
				),
			),
		],
	);
};

const folderForm = (
	h: H,
	model: Model,
	form: { mode: "create" | "rename"; value: string },
): Html => {
	const parent = Option.getOrElse(model.folder, () => "");
	return h.form(
		[
			h.DataAttribute("testid", "knowledge-folder-form"),
			h.Class("space-y-2 rounded-box bg-base-200/60 p-2"),
			h.OnSubmit(Message.SubmittedFolderForm()),
		],
		[
			h.label(
				[h.Class("block text-xs font-medium text-base-content/70")],
				[
					form.mode === "create"
						? parent === ""
							? i18next.t("knowledge.folders.newFolderTop")
							: i18next.t("knowledge.folders.newFolderIn", {
									folder: folderName(parent),
								})
						: i18next.t("knowledge.folders.renameHint"),
				],
			),
			h.input([
				h.Type("text"),
				h.Class("input input-sm w-full"),
				h.Id(FOLDER_NAME_ID),
				h.Value(form.value),
				h.Placeholder(
					form.mode === "create"
						? i18next.t("knowledge.folders.namePlaceholder")
						: "",
				),
				h.AriaLabel(
					i18next.t(
						form.mode === "create"
							? "knowledge.folders.nameLabel"
							: "knowledge.folders.pathLabel",
					),
				),
				h.OnInput((value) => Message.ChangedFolderName({ value })),
			]),
			h.div(
				[h.Class("flex justify-end gap-1")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-xs"),
							h.AriaLabel(i18next.t("knowledge.folders.cancelFormLabel")),
							h.OnClick(Message.CancelledFolderForm()),
						],
						[i18next.t("knowledge.folders.cancel")],
					),
					h.button(
						[
							h.Type("submit"),
							h.Class("btn btn-primary btn-xs"),
							h.AriaLabel(
								i18next.t(
									form.mode === "create"
										? "knowledge.folders.createLabel"
										: "knowledge.folders.renameLabel",
								),
							),
						],
						[
							i18next.t(
								form.mode === "create"
									? "knowledge.folders.create"
									: "knowledge.folders.rename",
							),
						],
					),
				],
			),
		],
	);
};

const folderPanel = (
	h: H,
	model: Model,
	shared: Shared,
	allCount: number,
	nodes: ReadonlyArray<FolderNode>,
): Html =>
	h.aside(
		[
			h.DataAttribute("testid", "knowledge-folders"),
			h.AriaLabel(i18next.t("knowledge.folders.title")),
			h.Class("space-y-2 lg:sticky lg:top-4 lg:self-start"),
		],
		[
			h.div(
				[h.Class("flex items-center justify-between px-2")],
				[
					h.h2(
						[
							h.Class(
								"text-xs font-semibold uppercase tracking-wide text-base-content/60",
							),
						],
						[i18next.t("knowledge.folders.title")],
					),
					Option.isSome(shared.asOf)
						? h.empty
						: h.button(
								[
									h.Type("button"),
									h.Class("btn btn-ghost btn-xs btn-square"),
									h.AriaLabel(i18next.t("knowledge.folders.newFolderLabel")),
									h.Title(i18next.t("knowledge.folders.newFolder")),
									h.OnClick(Message.ClickedNewFolder()),
								],
								[glyph(h, "FolderPlus")],
							),
				],
			),
			Option.match(model.folderForm, {
				onNone: () => h.empty,
				onSome: (form) =>
					form.mode === "create" ? folderForm(h, model, form) : h.empty,
			}),
			h.nav(
				[h.AriaLabel(i18next.t("knowledge.folders.treeLabel"))],
				[
					folderTreeView(h, {
						nodes,
						allCount,
						selected: Option.getOrNull(model.folder),
						toggled: model.toggled,
						href: (folder) => knowledgeHref(shared, folder),
						onToggle: (path) => Message.ToggledFolder({ path }),
						drop: Option.isSome(model.dragging)
							? {
									target: Option.getOrNull(model.dropTarget),
									onEnter: (folder) => Message.EnteredFolder({ folder }),
									onLeave: (folder) => Message.LeftFolder({ folder }),
									onDrop: (folder) => Message.DroppedOnFolder({ folder }),
								}
							: undefined,
					}),
				],
			),
			Option.isSome(model.dragging)
				? h.p(
						[
							h.DataAttribute("testid", "knowledge-drop-hint"),
							h.Role("status"),
							h.Class("px-2 text-xs text-base-content/60"),
						],
						[i18next.t("knowledge.folders.dropHint")],
					)
				: h.empty,
		],
	);

const folderHeading = (h: H, model: Model, shared: Shared): Html =>
	Option.match(model.folder, {
		onNone: () => h.empty,
		onSome: (folder) =>
			h.div(
				[h.Class("space-y-2")],
				[
					h.div(
						[h.Class("flex flex-wrap items-center justify-between gap-2")],
						[
							breadcrumb(h, shared, folder),
							Option.isSome(shared.asOf)
								? h.empty
								: h.button(
										[
											h.Type("button"),
											h.Class("btn btn-ghost btn-xs"),
											h.AriaLabel(
												i18next.t("knowledge.folders.renameFolderLabel", {
													folder: folderName(folder),
												}),
											),
											h.OnClick(Message.ClickedRenameFolder()),
										],
										[glyph(h, "Pencil"), i18next.t("knowledge.folders.rename")],
									),
						],
					),
					Option.match(model.folderForm, {
						onNone: () => h.empty,
						onSome: (form) =>
							form.mode === "rename" ? folderForm(h, model, form) : h.empty,
					}),
				],
			),
	});

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const all = notesOf(model);
	const notes = inFolder(all, model.folder);
	// The server's counts once loaded; until then, the loaded notes.
	const counts = AsyncData.getOrElse(model.folders, () => null);
	const nodes =
		counts === null
			? buildTree(all.map(folderOf), model.extraFolders)
			: treeFromCounts(counts, model.extraFolders);
	const allCount = counts === null ? all.length : countsTotal(counts);
	const shown =
		counts === null
			? notes.length
			: Option.match(model.folder, {
					onNone: () => allCount,
					onSome: (f) => nodes.find((n) => n.path === f)?.total ?? notes.length,
				});
	const searching = model.query.trim() !== "";
	return h.section(
		[
			h.DataAttribute("testid", "page-Knowledge"),
			h.Class("mx-auto w-full max-w-6xl space-y-4"),
		],
		[
			h.header(
				[h.Class("flex flex-wrap items-center justify-between gap-3")],
				[
					h.div(
						[h.Class("flex items-baseline gap-3")],
						[
							h.h1(
								[h.Class("text-2xl font-semibold")],
								[i18next.t("knowledge.title")],
							),
							AsyncData.isSuccess(model.notes) && notes.length > 0
								? h.span(
										[
											h.DataAttribute("testid", "knowledge-count"),
											h.Class("badge badge-ghost tabular-nums"),
											h.AriaLabel(
												i18next.t("knowledge.list.countLabel", {
													count: shown,
												}),
											),
										],
										[String(shown)],
									)
								: h.empty,
						],
					),
					newNoteButton(h, model, shared, true),
				],
			),
			Option.isSome(shared.asOf)
				? h.p(
						[
							h.DataAttribute("testid", "knowledge-live"),
							h.Class("text-sm text-base-content/60"),
						],
						[i18next.t("knowledge.list.liveData")],
					)
				: h.empty,
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) => status(h, error, "knowledge-create-error", true),
			}),
			Option.match(model.notice, {
				onNone: () => h.empty,
				onSome: ({ text, error }) =>
					status(h, text, "knowledge-folder-notice", error),
			}),
			h.div(
				[
					h.Class(
						"grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-start",
					),
				],
				[
					folderPanel(h, model, shared, allCount, nodes),
					h.div(
						[h.Class("min-w-0 space-y-4")],
						[
							folderHeading(h, model, shared),
							h.div(
								[h.Class("flex flex-wrap items-center gap-3")],
								[
									h.label(
										[h.Class("input input-bordered flex-1 min-w-56")],
										[
											h.span([h.Class("opacity-60")], [glyph(h, "Search")]),
											h.input([
												h.Type("search"),
												h.Class("grow"),
												h.Value(model.query),
												h.Placeholder(
													i18next.t("knowledge.list.searchPlaceholder"),
												),
												h.AriaLabel(i18next.t("knowledge.list.searchLabel")),
												h.OnInput((value) => Message.ChangedQuery({ value })),
											]),
										],
									),
									h.label(
										[h.Class("flex items-center gap-2 text-sm")],
										[
											h.input([
												h.Type("checkbox"),
												h.Class("toggle toggle-sm"),
												h.Checked(model.includeRecords),
												h.AriaLabel(
													i18next.t("knowledge.list.includeRecordsLabel"),
												),
												h.OnClick(Message.ToggledIncludeRecords()),
											]),
											h.span([], [i18next.t("knowledge.list.includeRecords")]),
										],
									),
								],
							),
							searching ? h.empty : tagFilter(h, model, tagCounts(notes)),
							h.div(
								[h.Class("card bg-base-100 border border-base-300 p-4")],
								[
									searching
										? results(h, model, shared)
										: AsyncData.match(model.notes, {
												onIdle: () => h.empty,
												onLoading: () =>
													status(
														h,
														i18next.t("knowledge.list.loading"),
														"knowledge-loading",
													),
												onFailure: () =>
													status(
														h,
														i18next.t("knowledge.list.loadError"),
														"knowledge-error",
														true,
													),
												onSuccess: (data) =>
													list(h, model, shared, data.notes, nodes),
												onRefreshing: (data) =>
													list(h, model, shared, data.notes, nodes),
												onStale: ({ data }) =>
													list(h, model, shared, data.notes, nodes),
											}),
								],
							),
						],
					),
				],
			),
		],
	);
});
